/**
 * Snow. Spec §7: "Snow types matter mechanically, not just visually. [...] This
 * is most of what makes conditions a real puzzle rather than a texture swap."
 *
 *   POWDER    — high resistance, high grip, floaty
 *   WIND SLAB — fast, unpredictable grip, prone to release
 *   CRUST     — fast until you break through
 *   ICE       — minimal grip, terrifying on steeps
 *
 * Stored per-cell as four normalised weights so transitions blend smoothly and
 * a cell can be genuinely half-and-half, which is what a real snowpack does.
 */

export interface SnowProps {
  readonly name: string
  /** Lateral grip coefficient — multiplies normal force to give max lateral accel (m/s²). */
  readonly grip: number
  /** Quadratic drag coefficient (per m). Dominates terminal speed. */
  readonly drag: number
  /** Linear resistance (1/s) — plough. This is what makes powder slow. */
  readonly resistance: number
  /** Amplitude of spatially-varying grip, 0..1. */
  readonly gripVariance: number
  /** How deep the skis ride, metres. Felt through camera and spray. */
  readonly sink: number
  /** Spray particles per second at reference speed. */
  readonly spray: number
  /** Extra speed scrubbed on a skid. */
  readonly skidDrag: number
  /**
   * How readily this surface releases loose snow downhill (spec §6). Powder on
   * a steep slope sluffs constantly; ice has nothing to give.
   */
  readonly sluff: number
  /**
   * Chance-weight that a hard slab sitting on this fails as a slab rather than
   * sluffing. Only wind slab has meaningful value here.
   */
  readonly slabRelease: number
  /** Breakable: crust supports you until it doesn't. 0 = never breaks. */
  readonly breakable: number
  /** Base surface colour. */
  readonly color: readonly [number, number, number]
}

export const POWDER: SnowProps = {
  name: 'powder',
  grip: 1.62, drag: 0.0094, resistance: 0.205, gripVariance: 0.05,
  sink: 0.55, spray: 190, skidDrag: 2.1,
  sluff: 1.0, slabRelease: 0.0, breakable: 0,
  color: [0.95, 0.97, 1.0],
}

export const WIND_SLAB: SnowProps = {
  name: 'wind slab',
  grip: 0.86, drag: 0.0088, resistance: 0.034, gripVariance: 0.34,
  sink: 0.08, spray: 45, skidDrag: 0.75,
  sluff: 0.25, slabRelease: 1.0, breakable: 0.25,
  color: [0.85, 0.885, 0.94],
}

export const CRUST: SnowProps = {
  name: 'crust',
  // Fast and grippy while it holds. `breakable` is what makes it a gamble:
  // punch through at speed and it grabs a ski and scrubs you hard.
  grip: 1.05, drag: 0.0082, resistance: 0.028, gripVariance: 0.18,
  sink: 0.04, spray: 22, skidDrag: 0.5,
  sluff: 0.1, slabRelease: 0.05, breakable: 1.0,
  color: [0.80, 0.83, 0.87],
}

export const ICE: SnowProps = {
  name: 'ice',
  grip: 0.32, drag: 0.0079, resistance: 0.012, gripVariance: 0.12,
  sink: 0.0, spray: 6, skidDrag: 0.18,
  sluff: 0.0, slabRelease: 0.0, breakable: 0,
  color: [0.72, 0.78, 0.84],
}

/** Not snow, but the solver needs numbers for it. */
export const ROCK: SnowProps = {
  name: 'rock',
  grip: 0.34, drag: 0.011, resistance: 0.02, gripVariance: 0.5,
  sink: 0, spray: 0, skidDrag: 0.4,
  sluff: 0.0, slabRelease: 0.0, breakable: 0,
  color: [0.29, 0.28, 0.30],
}

/** Fixed order. The per-cell weight array is laid out in exactly this order. */
export const SNOW_TYPES = [POWDER, WIND_SLAB, CRUST, ICE] as const
export const SNOW_TYPE_COUNT = SNOW_TYPES.length

export const enum Snow {
  Powder = 0,
  WindSlab = 1,
  Crust = 2,
  Ice = 3,
}

/** Scratch object so blending in the physics step allocates nothing. */
export interface SnowSample {
  grip: number
  drag: number
  resistance: number
  gripVariance: number
  sink: number
  spray: number
  skidDrag: number
  sluff: number
  slabRelease: number
  breakable: number
  /** 0..1 exposed rock. */
  rock: number
  /** Blended weights, same order as SNOW_TYPES. */
  readonly weights: Float32Array
  /** Name of the dominant constituent. */
  name: string
}

export function makeSnowSample(): SnowSample {
  const w = new Float32Array(SNOW_TYPE_COUNT)
  w[Snow.Powder] = 1
  return {
    grip: POWDER.grip, drag: POWDER.drag, resistance: POWDER.resistance,
    gripVariance: POWDER.gripVariance, sink: POWDER.sink, spray: POWDER.spray,
    skidDrag: POWDER.skidDrag, sluff: POWDER.sluff, slabRelease: POWDER.slabRelease,
    breakable: POWDER.breakable, rock: 0, weights: w, name: POWDER.name,
  }
}

/**
 * Blend `weights` (assumed normalised) into `out`, then let rock override.
 * Rock overrides rather than blends linearly, because half-rock is a real
 * surface but it should still feel like the mountain took your grip away.
 */
export function blendSnow(weights: ArrayLike<number>, rockT: number, out: SnowSample): void {
  let grip = 0, drag = 0, resistance = 0, variance = 0, sink = 0
  let spray = 0, skidDrag = 0, sluff = 0, slabRelease = 0, breakable = 0
  let best = 0
  let bestW = -1

  for (let i = 0; i < SNOW_TYPE_COUNT; i++) {
    const w = weights[i]
    out.weights[i] = w
    if (w <= 0) continue
    const t = SNOW_TYPES[i]
    grip += t.grip * w
    drag += t.drag * w
    resistance += t.resistance * w
    variance += t.gripVariance * w
    sink += t.sink * w
    spray += t.spray * w
    skidDrag += t.skidDrag * w
    sluff += t.sluff * w
    slabRelease += t.slabRelease * w
    breakable += t.breakable * w
    if (w > bestW) { bestW = w; best = i }
  }

  const r = rockT < 0 ? 0 : rockT > 1 ? 1 : rockT
  if (r > 0) {
    grip += (ROCK.grip - grip) * r
    drag += (ROCK.drag - drag) * r
    resistance += (ROCK.resistance - resistance) * r
    variance += (ROCK.gripVariance - variance) * r
    sink += (ROCK.sink - sink) * r
    spray += (ROCK.spray - spray) * r
    skidDrag += (ROCK.skidDrag - skidDrag) * r
    sluff *= 1 - r
    slabRelease *= 1 - r
    breakable *= 1 - r
  }

  out.grip = grip
  out.drag = drag
  out.resistance = resistance
  out.gripVariance = variance
  out.sink = sink
  out.spray = spray
  out.skidDrag = skidDrag
  out.sluff = sluff
  out.slabRelease = slabRelease
  out.breakable = breakable
  out.rock = r
  out.name = r > 0.5 ? ROCK.name : SNOW_TYPES[best].name
}
